import { useEffect, useState, type InputHTMLAttributes } from "react";
import { Chip } from "@vb/ui";
import { useGame } from "../store.js";
import {
  STRATEGIES,
  STRATEGY_BLURBS,
  STRATEGY_LABELS,
  type Strategy,
} from "../lib/autoplay-strategies.js";
import { formatCryptoCompact } from "../lib/format.js";
import {
  addPreset,
  deletePreset,
  loadPresets,
  type AutoplayPreset,
} from "../lib/presets.js";

interface AutoplayPanelProps {
  /** Disabled during a live round so the inputs don't move under the user. */
  disabled: boolean;
}

// =============================================================================
// NumInput — uncontrolled-feeling numeric input that lets the user type
// freely without the parent's reformatting fighting the cursor. Earlier
// version used `value={config.X.toFixed(N)}` which forced "1.50" back over a
// just-typed "2", making the field unusable. Here we keep a local string
// state, only call `onCommit` when the parsed number is finite, and never
// re-sync from props after first mount. Re-mount via React's natural
// state-reset (panel collapses to running view, then re-mounts on stop) takes
// care of "external" sync.
// =============================================================================
function NumInput({
  initial,
  onCommit,
  ...rest
}: {
  initial: number | null;
  onCommit: (n: number | null) => void;
} & Omit<InputHTMLAttributes<HTMLInputElement>, "value" | "onChange" | "type">) {
  const [text, setText] = useState(() =>
    initial === null || initial === undefined ? "" : String(initial),
  );
  return (
    <input
      type="text"
      inputMode="decimal"
      {...rest}
      value={text}
      onChange={(e) => {
        const v = e.target.value;
        setText(v);
        if (v.trim() === "") {
          onCommit(null);
          return;
        }
        const n = Number(v);
        if (Number.isFinite(n)) onCommit(n);
      }}
    />
  );
}

export function AutoplayPanel({ disabled }: AutoplayPanelProps) {
  const config = useGame((s) => s.autoplayConfig);
  const setConfig = useGame((s) => s.setAutoplayConfig);
  const autoplay = useGame((s) => s.autoplay);
  const startAutoplay = useGame((s) => s.startAutoplay);
  const stopAutoplay = useGame((s) => s.stopAutoplay);
  const balance = useGame((s) => s.balance);
  const currency = useGame((s) => s.currency);
  const [error, setError] = useState<string | null>(null);
  const [presets, setPresets] = useState<AutoplayPreset[]>(() => loadPresets());
  const [selectedPresetId, setSelectedPresetId] = useState<string>("");
  // Bumps every NumInput key so loading a preset triggers a re-mount and the
  // local string state in NumInput picks up the new initial values.
  const [presetSeq, setPresetSeq] = useState(0);

  // Reload from storage on mount so changes from other tabs are visible.
  useEffect(() => {
    setPresets(loadPresets());
  }, []);

  const manual = config.strategy === "manual";

  const selectedPreset = presets.find((p) => p.id === selectedPresetId) ?? null;

  const handleLoadPreset = (id: string): void => {
    setSelectedPresetId(id);
    if (!id) return;
    const p = presets.find((x) => x.id === id);
    if (!p) return;
    setConfig(p.config);
    setPresetSeq((n) => n + 1);
  };

  const handleSavePreset = (): void => {
    const name = window.prompt("Preset name?", "My strategy");
    if (!name?.trim()) return;
    const p = addPreset(name.trim(), config);
    setPresets(loadPresets());
    setSelectedPresetId(p.id);
  };

  const handleDeletePreset = (): void => {
    if (!selectedPreset || selectedPreset.builtin) return;
    if (!window.confirm(`Delete preset "${selectedPreset.name}"?`)) return;
    deletePreset(selectedPreset.id);
    setPresets(loadPresets());
    setSelectedPresetId("");
  };

  if (autoplay.active) {
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        <span className="label" style={{ fontSize: 10 }}>
          Autoplay running
        </span>
        <div
          style={{
            padding: "12px 14px",
            background: "var(--bg-2)",
            border: "1px solid var(--line)",
            borderRadius: 10,
            display: "grid",
            gap: 6,
          }}
        >
          <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13 }}>
            <span style={{ color: "var(--fg-2)" }}>Bets remaining</span>
            <span className="mono" style={{ color: "var(--fg-1)" }}>
              {autoplay.remaining === Number.POSITIVE_INFINITY ? "∞" : autoplay.remaining}
            </span>
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13 }}>
            <span style={{ color: "var(--fg-2)" }}>Current bet</span>
            <span className="mono" style={{ color: "var(--fg-1)" }}>
              {formatCryptoCompact(autoplay.currentBet, currency)} {currency}
            </span>
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13 }}>
            <span style={{ color: "var(--fg-2)" }}>Profit / loss</span>
            <span
              className="mono"
              style={{
                color: autoplay.cumulativeProfit >= 0 ? "var(--green)" : "var(--red)",
              }}
            >
              {autoplay.cumulativeProfit >= 0 ? "+" : ""}
              {formatCryptoCompact(autoplay.cumulativeProfit, currency)} {currency}
            </span>
          </div>
        </div>
        <button
          type="button"
          className="vb-btn"
          onClick={() => stopAutoplay("Stopped by user")}
          style={{
            padding: "10px 18px",
            fontSize: 13,
            background: "rgba(255,59,71,0.16)",
            border: "1px solid rgba(255,59,71,0.55)",
            color: "var(--red)",
            fontWeight: 700,
            letterSpacing: "0.08em",
            textTransform: "uppercase",
          }}
        >
          Stop autoplay
        </button>
        <p style={{ margin: 0, fontSize: 11, color: "var(--fg-3)", lineHeight: 1.5 }}>
          Stops the loop after the current round resolves. The active round still
          auto-cashouts at your target if it gets there.
        </p>
      </div>
    );
  }

  const submit = () => {
    setError(null);
    const r = startAutoplay();
    if (!r.ok) setError(r.reason ?? "Could not start");
  };

  const inputStyle = { padding: "8px 10px", fontSize: 13, width: "100%" } as const;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10, opacity: disabled ? 0.6 : 1 }}>
      <Field label="Preset">
        <div className="vb-autoplay-preset-row" style={{ display: "flex", gap: 6 }}>
          <select
            className="vb-input"
            value={selectedPresetId}
            onChange={(e) => handleLoadPreset(e.target.value)}
            disabled={disabled}
            style={{
              padding: "8px 10px",
              fontSize: 13,
              color: "var(--fg-1)",
              background: "var(--bg-2)",
              border: "1px solid var(--line)",
              borderRadius: 6,
              flex: 1,
              minWidth: 0,
            }}
          >
            <option value="">— select to load —</option>
            <optgroup label="Built-in">
              {presets
                .filter((p) => p.builtin)
                .map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
            </optgroup>
            {presets.some((p) => !p.builtin) && (
              <optgroup label="Saved">
                {presets
                  .filter((p) => !p.builtin)
                  .map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name}
                    </option>
                  ))}
              </optgroup>
            )}
          </select>
          <div className="vb-autoplay-preset-actions" style={{ display: "flex", gap: 6 }}>
            <button
              type="button"
              onClick={handleSavePreset}
              disabled={disabled}
              title="Save current settings as a new preset"
              style={{
                padding: "8px 10px",
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: "0.08em",
                textTransform: "uppercase",
                background: "var(--bg-2)",
                border: "1px solid var(--line)",
                borderRadius: 6,
                color: "var(--fg-2)",
                cursor: disabled ? "not-allowed" : "pointer",
              }}
            >
              Save
            </button>
            <button
              type="button"
              onClick={handleDeletePreset}
              disabled={disabled || !selectedPreset || selectedPreset.builtin}
              title={
                !selectedPreset
                  ? "Select a saved preset to delete"
                  : selectedPreset.builtin
                    ? "Built-in presets can't be deleted"
                    : `Delete "${selectedPreset.name}"`
              }
              style={{
                padding: "8px 10px",
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: "0.08em",
                textTransform: "uppercase",
                background: "transparent",
                border: "1px solid var(--line)",
                borderRadius: 6,
                color:
                  disabled || !selectedPreset || selectedPreset.builtin
                    ? "var(--fg-3)"
                    : "var(--red)",
                cursor:
                  disabled || !selectedPreset || selectedPreset.builtin
                    ? "not-allowed"
                    : "pointer",
              }}
            >
              Del
            </button>
          </div>
        </div>
      </Field>

      <Field label="Strategy">
        <select
          className="vb-input"
          value={config.strategy}
          onChange={(e) => setConfig({ strategy: e.target.value as Strategy })}
          disabled={disabled}
          style={{
            padding: "8px 10px",
            fontSize: 13,
            color: "var(--fg-1)",
            background: "var(--bg-2)",
            border: "1px solid var(--line)",
            borderRadius: 6,
            width: "100%",
          }}
        >
          {(Object.keys(STRATEGIES) as Strategy[]).map((s) => (
            <option key={s} value={s}>
              {STRATEGY_LABELS[s]}
            </option>
          ))}
        </select>
        <p style={{ margin: "4px 0 0", fontSize: 11, color: "var(--fg-3)", lineHeight: 1.5 }}>
          {STRATEGY_BLURBS[config.strategy]}
        </p>
      </Field>

      <Field label={`Base bet (${currency})`}>
        <NumInput
          key={`baseBet-${config.strategy}-${presetSeq}`}
          initial={config.baseBet}
          onCommit={(n) => n !== null && n >= 0 && setConfig({ baseBet: n })}
          className="vb-input"
          disabled={disabled}
          style={inputStyle}
        />
        <div style={{ display: "flex", gap: 4, marginTop: 4 }}>
          <Chip
            style={{ cursor: "pointer", padding: "3px 8px", fontSize: 11 }}
            onClick={() => setConfig({ baseBet: Number((config.baseBet / 2).toFixed(8)) })}
          >
            ½
          </Chip>
          <Chip
            style={{ cursor: "pointer", padding: "3px 8px", fontSize: 11 }}
            onClick={() => setConfig({ baseBet: Number((config.baseBet * 2).toFixed(8)) })}
          >
            2×
          </Chip>
          <Chip
            style={{ cursor: "pointer", padding: "3px 8px", fontSize: 11 }}
            onClick={() => setConfig({ baseBet: Number(Math.min(balance, 0.05).toFixed(8)) })}
          >
            Max
          </Chip>
        </div>
      </Field>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
        <Field label="Number of bets (0 = ∞)">
          <NumInput
            key={`numBets-${presetSeq}`}
            initial={config.numberOfBets}
            onCommit={(n) => {
              if (n === null) return;
              setConfig({ numberOfBets: Math.max(0, Math.min(1000, Math.floor(n))) });
            }}
            className="vb-input"
            disabled={disabled}
            style={inputStyle}
          />
        </Field>
        <Field label="Auto cashout @ ×">
          <NumInput
            key={`autoCashout-${presetSeq}`}
            initial={config.autoCashoutTarget}
            onCommit={(n) => n !== null && n >= 0 && setConfig({ autoCashoutTarget: n })}
            className="vb-input"
            disabled={disabled}
            style={inputStyle}
          />
        </Field>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
        <Field label="On win %">
          <NumInput
            key={`onWin-${presetSeq}`}
            initial={config.onWinPct}
            onCommit={(n) =>
              n !== null && setConfig({ onWinPct: Math.max(-100, Math.min(1000, n)) })
            }
            className="vb-input"
            disabled={disabled || !manual}
            title={manual ? undefined : "Disabled — strategy controls the bet adjustment"}
            style={inputStyle}
          />
        </Field>
        <Field label="On loss %">
          <NumInput
            key={`onLoss-${presetSeq}`}
            initial={config.onLossPct}
            onCommit={(n) =>
              n !== null && setConfig({ onLossPct: Math.max(-100, Math.min(1000, n)) })
            }
            className="vb-input"
            disabled={disabled || !manual}
            title={manual ? undefined : "Disabled — strategy controls the bet adjustment"}
            style={inputStyle}
          />
        </Field>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
        <Field label={`Stop on profit (${currency})`}>
          <NumInput
            key={`stopProfit-${presetSeq}`}
            initial={config.stopOnProfit}
            onCommit={(n) => setConfig({ stopOnProfit: n !== null && n > 0 ? n : null })}
            placeholder="off"
            className="vb-input"
            disabled={disabled}
            style={inputStyle}
          />
        </Field>
        <Field label={`Stop on loss (${currency})`}>
          <NumInput
            key={`stopLoss-${presetSeq}`}
            initial={config.stopOnLoss}
            onCommit={(n) => setConfig({ stopOnLoss: n !== null && n > 0 ? n : null })}
            placeholder="off"
            className="vb-input"
            disabled={disabled}
            style={inputStyle}
          />
        </Field>
      </div>

      <label
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          fontSize: 13,
          color: "var(--fg-2)",
          cursor: disabled ? "not-allowed" : "pointer",
          padding: "6px 0",
        }}
      >
        <input
          type="checkbox"
          checked={config.skipAnimations}
          disabled={disabled}
          onChange={(e) => setConfig({ skipAnimations: e.target.checked })}
          style={{ accentColor: "var(--gold-bright)", width: 16, height: 16 }}
        />
        <span>Skip win / bust cinematic</span>
        <span style={{ marginLeft: "auto", fontSize: 11, color: "var(--fg-3)" }}>
          Faster loop
        </span>
      </label>

      {error && (
        <div
          role="status"
          style={{
            padding: "6px 10px",
            background: "rgba(255,59,71,0.12)",
            border: "1px solid rgba(255,59,71,0.4)",
            color: "var(--red)",
            borderRadius: 6,
            fontSize: 12,
            fontFamily: "var(--font-mono)",
          }}
        >
          {error}
        </div>
      )}

      <button
        type="button"
        className="vb-btn vb-btn-gold"
        onClick={submit}
        disabled={disabled}
        style={{
          padding: "12px 18px",
          fontSize: 13,
          fontWeight: 700,
          letterSpacing: "0.08em",
          textTransform: "uppercase",
        }}
      >
        Start autoplay
      </button>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
      <span className="label" style={{ fontSize: 10 }}>
        {label}
      </span>
      {children}
    </div>
  );
}
